// =============================================================================
// iTravel 2.0 — useNativeSystemBars Hook
// =============================================================================
// Keeps the native status bar — and on Android the navigation bar — readable.
//
// It used to follow the theme alone: light mode meant dark icons, dark mode
// light ones. That holds only while the thing behind the bars is the app's own
// background, and stops holding the moment a screen puts something else there.
// The dark map in light mode was the visible case: dark icons on a dark map.
//
// So the theme is now the fallback, not the rule. A screen that knows what is
// behind the bars says so through useSystemBarContent, and that wins.
//
// No-ops on web.
// =============================================================================

import { useEffect } from 'react';

import { useResolvedMode } from '@/hooks/useResolvedTheme';
import { selectActiveClaim, useSystemBarsStore } from '@/store/system-bars.store';

const LIGHT_BACKGROUND = '#FFFFFF';
const DARK_BACKGROUND = '#0E1117';

/** Android draws its own bar behind the web view; transparent lets the content through. */
const TRANSPARENT = '#00000000';

export function useNativeSystemBars(): void {
  const mode = useResolvedMode();
  const claim = useSystemBarsStore(selectActiveClaim);

  const content = claim?.content ?? (mode === 'dark' ? 'dark' : 'light');
  const overlay = claim?.overlay ?? false;

  useEffect(() => {
    let cancelled = false;

    async function syncSystemBars() {
      try {
        const { Capacitor } = await import('@capacitor/core');

        if (!Capacitor.isNativePlatform() || cancelled) return;

        const { StatusBar, Style } = await import('@capacitor/status-bar');
        if (cancelled) return;

        // Style.Dark  → light icons, for dark content
        // Style.Light → dark icons, for light content
        //
        // Named after the content, not the icons, because the mapping between
        // the two is exactly what people get backwards.
        const isDarkContent = content === 'dark';
        await StatusBar.setStyle({ style: isDarkContent ? Style.Dark : Style.Light });

        // When a screen draws its own pixels up there, the web view has to
        // extend under the bar — otherwise the image stops short of the clock
        // and leaves a band of background.
        await StatusBar.setOverlaysWebView({ overlay }).catch(() => {
          // Not supported everywhere; the layout still works without it.
        });

        const backgroundColor = overlay
          ? TRANSPARENT
          : isDarkContent
            ? DARK_BACKGROUND
            : LIGHT_BACKGROUND;

        await StatusBar.setBackgroundColor({ color: backgroundColor }).catch(() => {
          // setBackgroundColor is Android-only — ignore on iOS
        });

        if (Capacitor.getPlatform() === 'android') {
          const { NavigationBar } = await import('@hugotomazi/capacitor-navigation-bar');
          if (cancelled) return;

          // The navigation bar sits at the bottom, where an overlaying screen
          // is usually still showing its own chrome — so it follows the theme
          // rather than the claim.
          const themeBackground = mode === 'dark' ? DARK_BACKGROUND : LIGHT_BACKGROUND;
          await NavigationBar.setColor({
            color: themeBackground,
            darkButtons: mode !== 'dark',
          }).catch(() => {
            // Plugin may not be registered yet on first launch — safe to ignore
          });
        }
      } catch {
        // Capacitor not available (running in browser) — silently ignore
      }
    }

    syncSystemBars();

    return () => {
      cancelled = true;
    };
  }, [content, overlay, mode]);
}
